import React from 'react';
import { useTranslation } from 'react-i18next';
import type { VisibilityReportFunnel } from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import { brandLabel } from './brandLabel';
import './ReportFunnel.scss';

interface ReportFunnelProps {
  funnel: VisibilityReportFunnel;
  brand: string;
}

/** Procent z zabezpieczeniem przed dzieleniem przez zero; jedno miejsce po przecinku, jak w makiecie. */
const share = (value: number, base: number): number =>
  base > 0 ? Math.round((value / base) * 1000) / 10 : 0;

/**
 * Lejek „mentioned → top-3 → top-1". Snapshot niesie surowe liczby odpowiedzi, więc wszystkie
 * procenty liczą się tutaj — dzięki temu stopnie lejka i przejścia między nimi nie mogą się
 * rozjechać zaokrągleniem.
 */
export const ReportFunnel: React.FC<ReportFunnelProps> = ({ funnel, brand }) => {
  const { t, i18n } = useTranslation();

  const { totalResponses, mentionedResponses, top3Responses, top1Responses } = funnel;

  // Slajd bez danych w ogóle nie wchodzi do raportu — zakres może nie mieć jeszcze odpowiedzi.
  if (totalResponses === 0) {
    return null;
  }

  const label = brandLabel(brand);
  const num = (value: number) => new Intl.NumberFormat(i18n.language).format(value);

  // Modyfikator niesie tło kafla; trzeci jest nasycony, więc ma własny wariant z białym tekstem.
  const stages = [
    {
      key: 'mentioned',
      modifier: 'primary',
      title: t('reportFunnelMentioned'),
      percent: share(mentionedResponses, totalResponses),
      desc: t('reportFunnelMentionedDesc', {
        responses: num(mentionedResponses),
        total: num(totalResponses),
        brand: label,
      }),
    },
    {
      key: 'top3',
      modifier: 'secondary',
      title: t('reportFunnelTop3'),
      percent: share(top3Responses, totalResponses),
      desc: t('reportFunnelTop3Desc', { responses: num(top3Responses) }),
    },
    {
      key: 'top1',
      modifier: 'brand',
      title: t('reportFunnelTop1'),
      percent: share(top1Responses, totalResponses),
      desc: t('reportFunnelTop1Desc', { responses: num(top1Responses), brand: label }),
    },
  ];

  // Przejścia liczone względem WZMIANEK, nie wszystkich odpowiedzi — to jest sedno lejka: ile z
  // tego, co już zdobyte, awansuje dalej.
  const steps = [
    {
      key: 'toTop3',
      modifier: 'primary',
      title: t('reportFunnelStepTop3'),
      desc: t('reportFunnelStepTop3Desc', { value: share(top3Responses, mentionedResponses) }),
    },
    {
      key: 'toTop1',
      modifier: 'brand',
      title: t('reportFunnelStepTop1'),
      desc: t('reportFunnelStepTop1Desc', { value: share(top1Responses, mentionedResponses) }),
    },
  ];

  return (
    <ReportSection
      title={t('reportFunnelTitle')}
      titleAccent={t('reportFunnelTitleAccent')}
      subtitle={t('reportFunnelSubtitle')}
    >
      <div className="report-funnel__stages">
        {stages.map((stage) => (
          <div
            key={stage.key}
            className={`report-funnel__stage report-funnel__stage--${stage.modifier}`}
          >
            <span className="report-funnel__stage-title">{stage.title}</span>
            <span className="report-funnel__stage-percent">{stage.percent}%</span>
            <span className="report-funnel__stage-desc">{stage.desc}</span>
          </div>
        ))}
      </div>

      <div className="report-funnel__steps">
        {steps.map((step) => (
          <div key={step.key} className={`report-funnel__step report-funnel__step--${step.modifier}`}>
            <span className="report-funnel__step-title">{step.title}</span>
            <span className="report-funnel__step-desc">{step.desc}</span>
          </div>
        ))}
      </div>
    </ReportSection>
  );
};

export default ReportFunnel;
